import React, { useEffect, useState } from "react";
import InfoPage from "../InfoPage";
import { SUMMIT_META_KEYS, getMetaEl } from "../InfoPage/SummitApplication";
import ShareProgram from "../../components/ShareProgram";
import RegistrationForm from "./RegistrationForm";
import Faq from "./Faq";
import {
  AUDIENCE,
  BENEFITS,
  CADENCE_LABEL,
  DATES_LABEL,
  FALLBACK_PRICES,
  OUTCOMES,
  PAGE_DESCRIPTION,
  PAGE_PATH,
  PAGE_TITLE,
  SCHEDULE,
  SESSION_TIME_LABEL,
  SHOW_LIMITED_SPOTS,
  buildFaqs,
  formatMoney,
} from "./content";
import "./InnerCircle.sass";

export const usePageMeta = (title, description) => {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const originalTitle = document.title;
    const originals = SUMMIT_META_KEYS.map((tag) => {
      const el = getMetaEl(tag);
      return { tag, el, value: el ? el.getAttribute("content") : null };
    });
    document.title = title;
    originals.forEach(({ tag, el }) => {
      if (!el) return;
      el.setAttribute("content", tag.key.endsWith("title") ? title : description);
    });
    return () => {
      document.title = originalTitle;
      originals.forEach(({ el, value }) => {
        if (!el) return;
        if (value == null) el.removeAttribute("content");
        else el.setAttribute("content", value);
      });
    };
  }, [title, description]);
};

export const useCohort = () => {
  const [cohort, setCohort] = useState(null);
  const [enrollment, setEnrollment] = useState("loading");
  useEffect(() => {
    let cancelled = false;
    fetch("/api/inner-circle/cohort")
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data?.ok && data.cohort) {
          setCohort(data.cohort);
          setEnrollment(data.cohort.enrollment || "closed");
        } else {
          setEnrollment("closed");
        }
      })
      .catch(() => {
        if (!cancelled) setEnrollment("closed");
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return { cohort, enrollment };
};

const scrollToSection = (id, focusId) => (e) => {
  if (e) e.preventDefault();
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  if (focusId) {
    window.setTimeout(() => document.getElementById(focusId)?.focus({ preventScroll: true }), 450);
  }
};

const InnerCircle = () => {
  usePageMeta(PAGE_TITLE, PAGE_DESCRIPTION);
  const { cohort, enrollment } = useCohort();

  useEffect(() => {
    if (enrollment === "loading") return;
    const id = window.location.hash.replace("#", "");
    if (!id) return;
    window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  }, [enrollment]);

  const currency = cohort?.currency || FALLBACK_PRICES.currency;
  const memberPrice = formatMoney(cohort?.memberPriceCents ?? FALLBACK_PRICES.memberPriceCents, currency);
  const regularPrice = formatMoney(cohort?.regularPriceCents ?? FALLBACK_PRICES.regularPriceCents, currency);
  const supportEmail = cohort?.supportEmail || "jamie@postaway.com";
  const isOpen = enrollment === "open";
  const sessionTime = cohort?.sessionTimeLabel
    ? `${cohort.sessionTimeLabel}${cohort.timezone ? ` (${cohort.timezone})` : ""}`
    : SESSION_TIME_LABEL;

  const body = (
    <div className="ic-page">
      <header className="ic-hero">
        <div className="ic-hero-copy">
        <h1 className="ic-hero-title">Postaway Inner Circle</h1>
        <p className="ic-hero-program-title">From Posting to Profit</p>
        <p className="ic-hero-subtitle">A hands-on social media experience for hosts and content creators</p>
        <p className="ic-hero-message">Stop guessing what to post. Start creating with purpose.</p>
        <ul className="ic-hero-meta" aria-label="Program at a glance">
          <li>{DATES_LABEL}</li>
          <li>{sessionTime}</li>
          <li>12 live sessions on Google Meet</li>
          <li>12 weekly challenges</li>
        </ul>
        <div className="ic-hero-actions">
          {enrollment === "loading" ? (
            <span className="ic-btn primary ic-btn-placeholder" aria-hidden="true">
              Save your spot
            </span>
          ) : isOpen ? (
            <a className="ic-btn primary" href="#register" onClick={scrollToSection("register", "ic-firstName")}>
              Save your spot
            </a>
          ) : (
            <a className="ic-btn primary" href="#tuition" onClick={scrollToSection("tuition")}>
              {enrollment === "full" ? "This cohort is full" : "Enrollment opens soon"}
            </a>
          )}
          <a className="ic-text-btn" href="#schedule" onClick={scrollToSection("schedule")}>
            See the full schedule
          </a>
        </div>
        <p className="ic-hero-price">
          {regularPrice} regular tuition · {memberPrice} for Postaway members
          {SHOW_LIMITED_SPOTS ? " · Limited spots" : ""}
        </p>
        </div>
        <figure className="ic-hero-collage">
          <div className="ic-photo ic-photo-main">
            <img src="/images/inner-circle/aframe.webp" width="960" height="1440" fetchPriority="high" alt="A glowing A-frame tucked into a lush forest, with a couple walking up the path" />
          </div>
          <div className="ic-photo ic-photo-pool">
            <img src="/images/inner-circle/pool.webp" width="640" height="1133" alt="An aerial view of friends floating in a turquoise pool framed by palms" />
          </div>
          <div className="ic-photo ic-photo-morning">
            <img src="/images/inner-circle/slow-morning.webp" width="640" height="924" alt="A quiet morning reading in a woven hanging chair" />
          </div>
          <span className="ic-collage-seal" aria-hidden="true">Find your<br />point of view.</span>
          <figcaption>A place for your next chapter.</figcaption>
        </figure>
      </header>

      <section className="ic-section" aria-labelledby="ic-audience-title">
        <h2 id="ic-audience-title" className="ic-section-title">
          Who it's for
        </h2>
        <div className="ic-card-grid ic-card-grid-2">
          {AUDIENCE.map((item) => (
            <div key={item.title} className="ic-card">
              <h3 className="ic-card-title">{item.title}</h3>
              <p className="ic-card-text">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="ic-section ic-inspiration" aria-labelledby="ic-inspiration-title">
        <div className="ic-inspiration-images">
          <figure className="ic-inspiration-wide">
            <img src="/images/inner-circle/cabin-moment.webp" width="900" height="1301" loading="lazy" decoding="async" alt="Golden light falling on a woman enjoying coffee outside a dark timber cabin" />
            <figcaption>The feeling of being there.</figcaption>
          </figure>
          <figure className="ic-inspiration-detail">
            <img src="/images/inner-circle/stay-details.webp" width="720" height="1080" loading="lazy" decoding="async" alt="Coffee, pastries and an open design book on a softly layered bed" />
            <figcaption>The details worth noticing.</figcaption>
          </figure>
        </div>
        <div className="ic-inspiration-copy">
          <div className="ic-eyebrow">See it differently</div>
          <h2 id="ic-inspiration-title" className="ic-section-title">Make them<br />feel the stay.</h2>
          <p className="ic-card-text">The quiet morning. The perfect corner. The moment someone saves for later.</p>
          <p className="ic-card-text">Learn to turn the places and experiences you love into content with a point of view—and a purpose.</p>
          <a className="ic-text-btn" href="#schedule" onClick={scrollToSection("schedule")}>Find your inspiration in the curriculum <span aria-hidden="true">↗</span></a>
        </div>
      </section>

      <section className="ic-section" aria-labelledby="ic-benefits-title">
        <h2 id="ic-benefits-title" className="ic-section-title">
          What you get
        </h2>
        <div className="ic-card-grid">
          {BENEFITS.map((item) => (
            <div key={item.title} className="ic-card ic-card-tint">
              <h3 className="ic-card-title">{item.title}</h3>
              <p className="ic-card-text">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="ic-section" aria-labelledby="ic-outcomes-title">
        <h2 id="ic-outcomes-title" className="ic-section-title">
          Where you'll be by April
        </h2>
        <ul className="ic-outcomes">
          {OUTCOMES.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="ic-section ic-community" aria-labelledby="ic-community-title">
        <div className="ic-eyebrow">Your kind of people</div>
        <h2 id="ic-community-title" className="ic-section-title">Create together. Go further.</h2>
        <p className="ic-section-intro">Bring your ideas, your questions and your unfinished drafts. Find hosts and creators who get it—and the encouragement to keep going.</p>
        <div className="ic-community-gallery">
          <figure className="ic-community-lead">
            <img src="/images/inner-circle/creator-circle.webp" width="800" height="1067" loading="lazy" decoding="async" alt="Five women sharing a joyful morning in a bright vacation-home kitchen" />
            <figcaption>Find your people.</figcaption>
          </figure>
          <figure>
            <img src="/images/inner-circle/fireside.webp" width="640" height="928" loading="lazy" decoding="async" alt="Friends laughing around a fire table on a cabin deck" />
            <figcaption>Share the spark.</figcaption>
          </figure>
          <figure>
            <img src="/images/inner-circle/summit-conversation.webp" width="640" height="924" loading="lazy" decoding="async" alt="Hosts and creators exchanging ideas at a Postaway Summit panel" />
            <figcaption>Learn from each other.</figcaption>
          </figure>
        </div>
        <p className="ic-community-note">A few moments from the Postaway community. Inner Circle comes together online, with live sessions on Google Meet and a private GroupMe group.</p>
      </section>

      <section className="ic-section" id="schedule" aria-labelledby="ic-schedule-title">
        <h2 id="ic-schedule-title" className="ic-section-title">
          The schedule
        </h2>
        <p className="ic-section-intro">
          {CADENCE_LABEL}, {sessionTime}, {DATES_LABEL}. Two scheduled breaks give you time to catch up on challenges.
        </p>
        <ol className="ic-schedule">
          {SCHEDULE.map((item) => (
            <li key={item.iso} className={`ic-session ${item.break ? "is-break" : ""}`}>
              <div className="ic-session-when">
                <span className="ic-session-num">{item.break ? "Break" : `Session ${item.session}`}</span>
                <time dateTime={item.iso} className="ic-session-date">
                  {item.date}
                </time>
              </div>
              <div className="ic-session-body">
                <h3 className="ic-session-title">{item.title}</h3>
                <p className="ic-session-text">{item.text}</p>
              </div>
            </li>
          ))}
        </ol>
        <figure className="ic-journal" aria-label="A little inspiration for the journey">
          <div className="ic-journal-photos">
            <img src="/IMG_0164.jpeg" width="640" height="1041" loading="lazy" decoding="async" alt="Sunlight streaming through golden aspen leaves" />
            <img src="/summit-2027-cabin.jpg" width="640" height="958" loading="lazy" decoding="async" alt="A warm timber cabin tucked among ferns and evergreens" />
            <img src="/images/IMG_8483.jpeg" width="640" height="418" loading="lazy" decoding="async" alt="A carefully set dining table with striped chairs and fresh greenery" />
          </div>
          <figcaption>Fresh places. Fresh perspectives.</figcaption>
        </figure>
      </section>

      <section className="ic-section" aria-labelledby="ic-format-title">
        <h2 id="ic-format-title" className="ic-section-title">
          Dates & format
        </h2>
        <dl className="ic-facts">
          <div>
            <dt>Dates</dt>
            <dd>
              {CADENCE_LABEL}, {DATES_LABEL}
            </dd>
          </div>
          <div>
            <dt>Breaks</dt>
            <dd>February 17 and March 17 (no live session)</dd>
          </div>
          <div>
            <dt>Session time</dt>
            <dd>{sessionTime}</dd>
          </div>
          <div>
            <dt>Live sessions</dt>
            <dd>Google Meet</dd>
          </div>
          <div>
            <dt>Community</dt>
            <dd>Private GroupMe group, invitation sent after your payment is confirmed</dd>
          </div>
        </dl>
      </section>

      <section className="ic-section" id="tuition" aria-labelledby="ic-tuition-title">
        <h2 id="ic-tuition-title" className="ic-section-title">
          Tuition
        </h2>
        <div className="ic-card-grid ic-card-grid-2">
          <div className="ic-card ic-price-card ic-price-regular">
            <h3 className="ic-card-title">Regular tuition</h3>
            <div className="ic-price">{regularPrice}</div>
            <p className="ic-card-text">Open to every host and creator. No Postaway membership required.</p>
          </div>
          <div className="ic-card ic-price-card ic-price-member">
            <h3 className="ic-card-title">Postaway members</h3>
            <div className="ic-price">{memberPrice}</div>
            <p className="ic-card-text">Register with the email on your Postaway account and the member rate is applied automatically.</p>
          </div>
        </div>
        <p className="ic-section-note">One-time payment for the January 2027 cohort. No automatic renewal.</p>
      </section>

      <section className="ic-section ic-register-section" id="register" aria-labelledby="ic-register-title">
        <h2 id="ic-register-title" className="ic-section-title">
          Save your spot
        </h2>
        <RegistrationForm cohort={cohort} enrollment={enrollment} />
      </section>

      <section className="ic-section" aria-labelledby="ic-faq-title">
        <h2 id="ic-faq-title" className="ic-section-title">
          FAQs
        </h2>
        <Faq items={buildFaqs({ memberPrice, regularPrice, supportEmail })} />
        <p className="ic-section-note">
          More questions? Email <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
        </p>
      </section>

      <section className="ic-section ic-share-band ic-share-with-photo" aria-labelledby="ic-share-title">
        <figure className="ic-share-photo">
          <img src="/images/inner-circle/summit-panel-group.jpeg" width="1206" height="1740" loading="lazy" decoding="async" alt="Eight Postaway hosts and creators sharing ideas together on a Summit panel" />
          <figcaption>Good things are better shared.</figcaption>
        </figure>
        <div className="ic-share-copy">
        <h2 id="ic-share-title" className="ic-section-title">
          Know a host or creator who'd love this?
        </h2>
        <ShareProgram
          path={PAGE_PATH}
          title="Postaway Inner Circle"
          text="From Posting to Profit: a hands-on social media program for hosts and creators."
          placement="page"
        />
        </div>
      </section>
    </div>
  );

  return <div className="ic-landing"><InfoPage below={body} disableStamps /></div>;
};

export default InnerCircle;
